import { useId } from 'react';

/*
 * Ícones de jogo "3D" em SVG — recriados com gradientes radiais, brilho
 * especular e sombra própria para dar volume. Os IDs de gradiente usam
 * useId() para poderem coexistir várias instâncias na mesma página.
 */

export function Coin3D({ className, style }) {
  const id = useId();
  return (
    <svg viewBox="0 0 32 32" className={className} style={style} aria-hidden>
      <defs>
        <radialGradient id={`${id}-body`} cx="35%" cy="28%" r="80%">
          <stop offset="0%" stopColor="#FFF3B0" />
          <stop offset="35%" stopColor="#FFD73E" />
          <stop offset="75%" stopColor="#F5A623" />
          <stop offset="100%" stopColor="#C87B0A" />
        </radialGradient>
        <linearGradient id={`${id}-rim`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFE98A" />
          <stop offset="100%" stopColor="#A05E00" />
        </linearGradient>
      </defs>
      {/* espessura da moeda */}
      <ellipse cx="16" cy="17.5" rx="14" ry="13" fill="#8A5200" />
      <circle cx="16" cy="15.5" r="14" fill={`url(#${id}-rim)`} />
      <circle cx="16" cy="15.5" r="12" fill={`url(#${id}-body)`} stroke="#B8860B" strokeWidth="0.8" />
      <circle cx="16" cy="15.5" r="9.2" fill="none" stroke="#8A5200" strokeWidth="1" opacity="0.55" />
      <text x="16" y="20.5" textAnchor="middle" fontSize="13" fontWeight="900" fill="#7A4E00" fontFamily="'Syne',sans-serif">H</text>
      {/* brilho especular */}
      <ellipse cx="11" cy="9.5" rx="4.6" ry="2.6" fill="#FFFFFF" opacity="0.75" transform="rotate(-24 11 9.5)" />
      <circle cx="22.5" cy="21" r="1.1" fill="#FFF6C9" opacity="0.8" />
    </svg>
  );
}

export function Gem3D({ className, style }) {
  const id = useId();
  return (
    <svg viewBox="0 0 32 32" className={className} style={style} aria-hidden>
      <defs>
        <linearGradient id={`${id}-g`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#E9D5FF" />
          <stop offset="45%" stopColor="#A855F7" />
          <stop offset="100%" stopColor="#5B21B6" />
        </linearGradient>
      </defs>
      <ellipse cx="16" cy="28" rx="9" ry="2.4" fill="#000" opacity="0.25" />
      <path d="M16 3 27 12 16 29 5 12Z" fill={`url(#${id}-g)`} stroke="#4C1D95" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M5 12h22" stroke="#DDD6FE" strokeWidth="1" opacity="0.9" />
      <path d="M16 3l-5 9 5 17M16 3l5 9-5 17" stroke="#C4B5FD" strokeWidth="0.9" opacity="0.8" fill="none" />
      <path d="M16 3 11 12h10Z" fill="#F3E8FF" opacity="0.5" />
      <ellipse cx="12.2" cy="8" rx="2.2" ry="1.1" fill="#fff" opacity="0.85" transform="rotate(-32 12.2 8)" />
    </svg>
  );
}

export function Energy3D({ className, style }) {
  const id = useId();
  return (
    <svg viewBox="0 0 32 32" className={className} style={style} aria-hidden>
      <defs>
        <linearGradient id={`${id}-e`} x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#FEF9C3" />
          <stop offset="45%" stopColor="#FACC15" />
          <stop offset="100%" stopColor="#EA580C" />
        </linearGradient>
      </defs>
      <ellipse cx="16" cy="29" rx="7" ry="2" fill="#000" opacity="0.25" />
      <path d="M18 2 6 18h8l-2 12L26 13h-9l1-11Z" fill={`url(#${id}-e)`} stroke="#C2410C" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M17.2 4.6 8.6 16.4" stroke="#FFFDE7" strokeWidth="1.6" strokeLinecap="round" opacity="0.85" />
    </svg>
  );
}

export function Chest3D({ className, style }) {
  const id = useId();
  return (
    <svg viewBox="0 0 40 40" className={className} style={style} aria-hidden>
      <defs>
        <linearGradient id={`${id}-lid`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C98A4B" />
          <stop offset="100%" stopColor="#8A5A28" />
        </linearGradient>
        <linearGradient id={`${id}-box`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#A9713A" />
          <stop offset="100%" stopColor="#6E4519" />
        </linearGradient>
        <radialGradient id={`${id}-glow`} cx="50%" cy="30%" r="60%">
          <stop offset="0%" stopColor="#FFE98A" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#FFD73E" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}-metal`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFE98A" />
          <stop offset="100%" stopColor="#B8860B" />
        </linearGradient>
      </defs>
      <ellipse cx="20" cy="36.5" rx="15" ry="2.6" fill="#000" opacity="0.28" />
      {/* brilho a sair do baú */}
      <ellipse cx="20" cy="16" rx="15" ry="9" fill={`url(#${id}-glow)`} />
      {/* tampa aberta */}
      <path d="M6 15c0-5 6.5-8 14-8s14 3 14 8l-1.5 3H7.5Z" fill={`url(#${id}-lid)`} stroke="#4E2E0E" strokeWidth="1.2" />
      <path d="M8.5 13.4C9.5 10 14 8.6 20 8.6s10.5 1.4 11.5 4.8" stroke="#E7B77C" strokeWidth="1" fill="none" opacity="0.7" />
      {/* moedas dentro */}
      <circle cx="14.5" cy="17.5" r="3" fill="#FFD73E" stroke="#B8860B" strokeWidth="0.8" />
      <circle cx="20.5" cy="16.5" r="3.4" fill="#FFE066" stroke="#B8860B" strokeWidth="0.8" />
      <circle cx="26.5" cy="17.8" r="2.8" fill="#F5A623" stroke="#B8860B" strokeWidth="0.8" />
      {/* corpo */}
      <rect x="7" y="19" width="26" height="14" rx="2.5" fill={`url(#${id}-box)`} stroke="#4E2E0E" strokeWidth="1.2" />
      <rect x="7" y="19" width="26" height="3.6" fill="#5C3A14" opacity="0.55" />
      {/* ferragens */}
      <rect x="10" y="19" width="2.4" height="14" fill={`url(#${id}-metal)`} opacity="0.9" />
      <rect x="27.6" y="19" width="2.4" height="14" fill={`url(#${id}-metal)`} opacity="0.9" />
      {/* fechadura */}
      <rect x="16.8" y="20.5" width="6.4" height="7.6" rx="1.6" fill={`url(#${id}-metal)`} stroke="#7A4E00" strokeWidth="0.9" />
      <circle cx="20" cy="23.4" r="1.3" fill="#7A4E00" />
      <path d="M20 24.4v2.1" stroke="#7A4E00" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

export function StarXP({ className, style }) {
  const id = useId();
  return (
    <svg viewBox="0 0 32 32" className={className} style={style} aria-hidden>
      <defs>
        <linearGradient id={`${id}-s`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#BAE6FD" />
          <stop offset="50%" stopColor="#38BDF8" />
          <stop offset="100%" stopColor="#0369A1" />
        </linearGradient>
      </defs>
      <ellipse cx="16" cy="29" rx="8" ry="2" fill="#000" opacity="0.22" />
      <path d="M16 2.5 20 11.4l9.7 1-7.2 6.6 2 9.5L16 23.6l-8.5 4.9 2-9.5-7.2-6.6 9.7-1Z"
        fill={`url(#${id}-s)`} stroke="#075985" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M16 5.8 13.4 11.4l-6 0.7" stroke="#E0F2FE" strokeWidth="1.1" strokeLinecap="round" fill="none" opacity="0.8" />
    </svg>
  );
}

export default { Coin3D, Gem3D, Energy3D, Chest3D, StarXP };
